import React, { useState, useEffect, useCallback } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import PlanOption from './PlanOption';
import './Stripe.css';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

// Load Stripe with your publishable API key
const stripePromise = loadStripe(`${process.env.REACT_APP_STRIPE_API_KEY}`);

export const PaymentInfo = ({ username }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [hasStripeId, setHasStripeId] = useState(false); // Track if Stripe ID exists
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [fetchingSubscription, setFetchingSubscription] = useState(false);

  // Define the checkStripeId function with useCallback to control when it triggers
  const checkStripeId = useCallback(async () => {
    if (username) { // Ensure username is defined before making the call
      console.log(username);
      try {
        const response = await axios.get(`${process.env.REACT_APP_QMI_BASE_API}/api/stripe/check-stripe-id?email=${encodeURIComponent(username)}`);
        setHasStripeId(response.data.hasStripeId);
      } catch (error) {
        setErrorMessage('Unable to check subscription status.');
      }
    }
  }, [username]);

  // Only trigger checkStripeId when username is set
  useEffect(() => {
    if (username) {
      checkStripeId();
    }
  }, [username, checkStripeId]);

// Fetch customer and subscription information only if hasStripeId is true
useEffect(() => {
  const fetchCustomerAndSubscriptionInfo = async () => {
    setFetchingSubscription(true); // Set loading state for fetching subscription info
    try {
      const subscriptionResponse = await axios.get(`${process.env.REACT_APP_QMI_BASE_API}/api/stripe/get-subscription-info?email=${encodeURIComponent(username)}`);
      setSubscriptionInfo(subscriptionResponse.data[0]);

      const customerResponse = await axios.get(`${process.env.REACT_APP_QMI_BASE_API}/api/stripe/get-customer-info?email=${encodeURIComponent(username)}`);
      setCustomerInfo(customerResponse.data);
    } catch (error) {
      setErrorMessage('Unable to fetch account information.');
    } finally {
      setFetchingSubscription(false); // Stop loading once info is fetched
    }
  };

  if (hasStripeId) {
    fetchCustomerAndSubscriptionInfo();
  }
}, [username, hasStripeId]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const success = queryParams.get('success');
    const canceled = queryParams.get('canceled');

    if (success === 'true') {
      setPaymentSuccess(true);
      setShowForm(false);
      navigate(location.pathname, { replace: true });
    }

    if (canceled === 'true') {
      setErrorMessage('Payment was canceled. Please try again.');
      setShowForm(true);
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, navigate, location.pathname]);

  const priceIds = {
    monthly: 'price_1QAydQI0QSnHmHlUqBpfmdcS',
    yearly: 'price_1QAyV1I0QSnHmHlUMCVxT9ce',
  };

  const handleCheckout = async () => {
    setLoading(true);
    setErrorMessage(null);
    const stripe = await stripePromise;

    try {
      // Encode the email to safely send it in the request
      const encodedEmail = encodeURIComponent(username);

      const response = await axios.post(`${process.env.REACT_APP_QMI_BASE_API}/api/stripe/create-checkout-session`, {
        email: username,
        priceId: priceIds[selectedPlan],
      });

      if (response.data.customerPortalUrl) {
        window.location.href = response.data.customerPortalUrl;
      } else if (response.data.sessionId) {
        const { error } = await stripe.redirectToCheckout({ sessionId: response.data.sessionId });
        if (error) setErrorMessage(error.message);
      }
    } catch (error) {
      setErrorMessage('There was an issue creating the checkout session.');
    } finally {
      setLoading(false);
    }
  };

  const handleCustomerPortal = async () => {
    setLoading(true);
    setErrorMessage(null);

    try {
      // Encode the email for safe transmission
      const encodedEmail = encodeURIComponent(username);

      // Send only the email to request the customer portal session
      const response = await axios.post(`${process.env.REACT_APP_QMI_BASE_API}/api/stripe/create-customer-portal-session`, {
        email: username,  // Use encoded email here
      });

      // Redirect to the Stripe Customer Portal
      window.location.href = response.data.url;
    } catch (error) {
      setErrorMessage('There was an issue opening the Customer Portal.');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnToForm = () => {
    setShowForm(true);
    setPaymentSuccess(false);
  };

  if (!showForm && paymentSuccess) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
        <h1>Thank you for your purchase!</h1>
        <p>Your subscription has been successfully activated.</p>
        <button onClick={handleReturnToForm} className="btn btn-secondary" style={{ marginTop: '20px' }}>
          Return to Form
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '600px', margin: '0 auto' }}>
      {hasStripeId ? (
        fetchingSubscription ? (
          <p>Loading subscription details...</p>
        ) : customerInfo && subscriptionInfo ? (
          <div>
            <h2>Account and Subscription Information</h2>
            <p><strong>Email:</strong> {customerInfo.email}</p>
            <p><strong>Subscription Status:</strong> {subscriptionInfo.status}</p>
            <p><strong>Recurring Price:</strong> ${subscriptionInfo.items[0].price.unitAmount / 100}.00</p>
            <p><strong>Recurs:</strong> {subscriptionInfo.items[0].price.recurring.interval}ly</p>
            <p><strong>Current Period End:</strong> {new Date(subscriptionInfo.currentPeriodEnd).toLocaleDateString()}</p>

            <button
              onClick={handleCustomerPortal}
              className="btn btn-secondary"
              style={{ marginTop: '10px' }}
            >
              Manage Subscription
            </button>
          </div>
        ) : (
          <p>Unable to load subscription details at this time.</p>
        )
      ) : (
        <div>
          <h2>Select Your Subscription Plan</h2>
          <PlanOption
            planName="monthly"
            price="$25"
            billingCycle="Billed Monthly"
            description="This is a great option for flexibility."
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
          />
          <PlanOption
            planName="yearly"
            price="$225"
            billingCycle="Billed Annually"
            description="Best value! Save $25 a year."
            selectedPlan={selectedPlan}
            setSelectedPlan={setSelectedPlan}
          />
          <button
            onClick={handleCheckout}
            disabled={loading}
            className="btn btn-primary"
            style={{ marginTop: '20px' }}
          >
            {loading ? 'Processing...' : 'Subscribe Now'}
          </button>
        </div>
      )}
      {errorMessage && <div style={{ color: 'red', marginTop: '10px' }}>{errorMessage}</div>}
    </div>
  );
};

export default PaymentInfo;
